import { Client } from "@stomp/stompjs";
import SockJS from "sockjs-client";
import variables from "globals/variables";
import loggerFactory from "globals/logger/logger-factory";
import AuthenticationService from "application/modules/authentication/AuthenticationService";

class RealtimeUpdate {
  constructor() {
    this.services = null;
    this.telemetry = null;
    this.nms = null;
    this.mcx = null;
    this.dispatcher = null;

    this.connected = false;
    this.mcxConnected = false;

    this.nmsEvents = [];
    this.mcxEvents = [];
    this.serviceEvents = [];
    this.telemetryEvents = [];
    this.routeCoordinateEvents = [];
    this.dispatcherEvents = [];
    this.mcxSubscriptions = new Set();
    this.serviceSubscriptions = new Set();
    this.onMCXMessage = this.onMCXUpdate.bind(this);
  }

  subscribeServiceTopic(topic) {
    if (!topic || !this.services || !this.services.connected) {
      return;
    }

    if (this.serviceSubscriptions.has(topic)) {
      return;
    }

    this.services.subscribe(topic, this.onServiceUpdate);
    this.serviceSubscriptions.add(topic);
  }

  connect() {
    if (
      AuthenticationService.allownmsMenus() ||
      AuthenticationService.allownmsViewerMenus() ||
      AuthenticationService.allownmsAdminMenus() ||
      AuthenticationService.allowAdminMenus()
    ) {
      this.nmsEvents.push(`/app/v2/messaging/topic/events`);
    }

    if (
      AuthenticationService.allowUCConferenceMenus() ||
      AuthenticationService.allowMCXDispatcher() ||
      AuthenticationService.allowRailwayDispatcher()
    ) {
      const mcxTopic = `/app/v2/messaging/topic/mcx${AuthenticationService.getExtensionNumber()}`;

      if (!this.mcxEvents.includes(mcxTopic)) {
        this.mcxEvents.push(mcxTopic);
      }

      // Add conference topics for each department
      const departments = AuthenticationService.getDepartmentList();
      if (departments && Array.isArray(departments)) {
        departments.forEach((dept) => {
          if (dept.extension) {
            const conferenceTopic = `/app/v2/messaging/topic/conference-${dept.extension}`;
            if (!this.mcxEvents.includes(conferenceTopic)) {
              this.mcxEvents.push(conferenceTopic);
              loggerFactory.debug(
                "MCX Conference Topics",
                `[ADDED] Conference topic for department: ${dept.department} (${dept.extension})`,
                `Topic: ${conferenceTopic}`
              );
            }
          }
        });
      }
    }

    this.serviceEvents.push(
      `/app/v2/messaging/topic/user-${AuthenticationService.getUserName()}`
    );

    this.telemetryEvents.push(
      `/app/v2/messaging/topic/user-${AuthenticationService.getUserName()}`
    );

    this.telemetryEvents.push(`/app/v2/messaging/topic/routeCoordinate/live`);

    if (
      AuthenticationService.allowMCXDispatcher() ||
      AuthenticationService.allowRailwayDispatcher()
    ) {
      this.dispatcherEvents.push(
        `/app/v2/messaging/topic/dispatcher/notifications/user/${AuthenticationService.getUserName()}`
      );
    }

    if (AuthenticationService.getAuthenticationToken()) {
      this.services = new Client({
        brokerURL: variables.webSocket.services,
        debug: function (str) {
          loggerFactory.debug(
            this.componentName,
            "Web Socket Event : Services",
            str
          );
        },
        reconnectDelay: 5000,
        heartbeatIncoming: 4000,
        heartbeatOutgoing: 4000,
        connectHeaders: {
          Authorization: `Bearer ${AuthenticationService.getAuthenticationToken()}`,
        },
        webSocketFactory: () => {
          const sock = new SockJS(variables.webSocket.services);
          return sock;
        },
        onConnect: () => {
          this.serviceSubscriptions.clear();
          this.serviceEvents.forEach((topic) => {
            this.subscribeServiceTopic(topic);
          });
        },
        onDisconnect: () => {
          this.serviceSubscriptions.clear();
        },
      });

      this.telemetry = new Client({
        brokerURL: variables.webSocket.telemetry,
        debug: function (str) {
          loggerFactory.debug(
            this.componentName,
            "Web Socket Event : telemetry",
            str
          );
        },
        reconnectDelay: 5000,
        heartbeatIncoming: 4000,
        heartbeatOutgoing: 4000,
        connectHeaders: {
          Authorization: `Bearer ${AuthenticationService.getAuthenticationToken()}`,
        },
        webSocketFactory: () => {
          const sock = new SockJS(variables.webSocket.telemetry);
          return sock;
        },
        onConnect: () => {
          this.telemetry.subscribe(
            `/app/v2/messaging/topic/user-${AuthenticationService.getUserName()}`,
            this.onTelemetryUpdate
          );

          this.telemetry.subscribe(
            `/app/v2/messaging/topic/routeCoordinate/live`,
            this.onRouteCoordinateUpdate
          );
        },
      });

      this.nms = new Client({
        brokerURL: variables.webSocket.telemetry,
        debug: function (str) {
          loggerFactory.debug(
            this.componentName,
            "Web Socket Event : NMS",
            str
          );
        },
        reconnectDelay: 5000,
        heartbeatIncoming: 4000,
        heartbeatOutgoing: 4000,
        connectHeaders: {
          Authorization: `Bearer ${AuthenticationService.getAuthenticationToken()}`,
        },
        webSocketFactory: () => {
          const sock = new SockJS(variables.webSocket.telemetry);
          return sock;
        },
        onConnect: () => {
          this.nms.subscribe(
            `/app/v2/messaging/topic/events`,
            this.onNMSUpdate
          );
        },
      });

      this.mcx = this.createMCXClient(variables.webSocket.mcx);

      if (
        AuthenticationService.allowMCXDispatcher() ||
        AuthenticationService.allowRailwayDispatcher()
      ) {
        this.dispatcher = new Client({
          brokerURL: variables.webSocket.services,
          debug: function (str) {
            loggerFactory.debug(
              this.componentName,
              "Web Socket Event : Dispatcher",
              str
            );
          },
          reconnectDelay: 5000,
          heartbeatIncoming: 4000,
          heartbeatOutgoing: 4000,
          connectHeaders: {
            Authorization: `Bearer ${AuthenticationService.getAuthenticationToken()}`,
          },
          webSocketFactory: () => {
            const sock = new SockJS(variables.webSocket.services);
            return sock;
          },
          onConnect: () => {
            this.dispatcherEvents.forEach((topic) => {
              this.dispatcher.subscribe(topic, this.onDispatcherUpdate);
            });
          },
        });
      }

      this.connected = true;

      if (this.serviceEvents.length > 0) {
        this.services.activate();
      }

      if (this.telemetryEvents.length > 0) {
        this.telemetry.activate();
      }

      if (this.nmsEvents.length > 0) {
        this.nms.activate();
      }

      if (this.mcxEvents.length > 0) {
        this.mcx.activate();
      }

      if (this.dispatcherEvents.length > 0 && this.dispatcher) {
        this.dispatcher.activate();
      }
    }
  }

  createMCXClient(brokerURL) {
    return new Client({
      brokerURL,
      debug: function (str) {
        loggerFactory.debug(this.componentName, "Web Socket Event : MCX", str);
      },
      reconnectDelay: 5000,
      heartbeatIncoming: 4000,
      heartbeatOutgoing: 4000,
      connectHeaders: {
        Authorization: `Bearer ${AuthenticationService.getAuthenticationToken()}`,
      },
      webSocketFactory: () => new SockJS(brokerURL),
      onConnect: () => {
        loggerFactory.debug("MCX", "STOMP connection established.");
        this.mcxConnected = true;
        this.mcxSubscriptions.clear();
        this.mcxEvents.forEach((event) => {
          this.subscribeMCXTopic(event);
        });
      },
      onDisconnect: () => {
        this.mcxConnected = false;
        this.mcxSubscriptions.clear();
        loggerFactory.debug("MCX", "STOMP connection closed.");
      },
    });
  }

  subscribeMCXTopic(topic) {
    if (!topic || !this.mcx || !this.mcxConnected) {
      return;
    }

    if (this.mcxSubscriptions.has(topic)) {
      return;
    }

    this.mcx.subscribe(topic, this.onMCXMessage);
    this.mcxSubscriptions.add(topic);
  }

  async connectMCX(server) {
    loggerFactory.info("MCX connecting with server", server);

    if (this.mcxConnected) {
      await this.mcx.deactivate();
      this.mcxConnected = false;
    }

    this.mcx = this.createMCXClient(
      server
        ? `${window.location.protocol}//${server}/ws/ppdr/app/v2/messaging/messages`
        : variables.webSocket.mcx
    );

    if (this.mcxEvents.length > 0) {
      this.mcx.activate();
    }
  }

  sendMCX(payload) {
    if (this.mcx && this.mcxConnected) {
      this.mcx.publish({
        destination: "/app/v2/messaging/app/ptt-message",
        body: JSON.stringify(payload),
      });
    } else {
      console.warn("MCX websocket not connected yet");
    }
  }

  receiveMessageFor(topic, callback, callbackId) {
    if (!callbackId) {
      callbackId = topic;
    }

    if (topic === "events") {
      AuthenticationService.nmsCallBacks.set(callbackId, {
        eventTopic: `/app/v2/messaging/topic/${topic}`,
        eventCallback: callback,
      });
    }

    if (!this.connected) {
      this.connect();
    }

    return true;
  }

  receiveMessageForMCX(topic, callback, callbackId) {
    if (!callbackId) {
      callbackId = topic;
    }

    const eventTopic = topic?.startsWith("/app/v2/messaging/topic/")
      ? topic
      : `/app/v2/messaging/topic/${topic || `mcx${AuthenticationService.getExtensionNumber()}`}`;

    AuthenticationService.mcxCallBacks.set(callbackId, {
      eventTopic,
      eventCallback: callback,
    });

    if (!this.mcxEvents.includes(eventTopic)) {
      this.mcxEvents.push(eventTopic);
    }

    if (!this.connected) {
      this.connect();
      return true;
    }

    if (!this.mcx) {
      this.mcx = this.createMCXClient(variables.webSocket.mcx);
    }

    if (!this.mcxConnected) {
      if (!this.mcx.active) {
        this.mcx.activate();
      }
      return true;
    }

    this.subscribeMCXTopic(eventTopic);

    return true;
  }

  receiveMessageForService(topic, callback, callbackId) {
    if (!callbackId) {
      callbackId = topic;
    }

    const eventTopic = `/app/v2/messaging/topic/${topic}`;

    AuthenticationService.serviceCallBacks.set(callbackId, {
      eventTopic,
      eventCallback: callback,
    });

    if (!this.serviceEvents.includes(eventTopic)) {
      this.serviceEvents.push(eventTopic);
    }

    this.subscribeServiceTopic(eventTopic);

    if (!this.connected) {
      this.connect();
    }

    return true;
  }

  receiveMessageForTelemetry(topic, callback, callbackId) {
    if (!callbackId) {
      callbackId = topic;
    }

    AuthenticationService.telemetryCallBacks.set(callbackId, {
      eventTopic: `/app/v2/messaging/topic/${topic}`,
      eventCallback: callback,
    });

    if (!this.connected) {
      this.connect();
    }

    return true;
  }

  receiveMessageForDispatcher(callback, callbackId) {
    const username = AuthenticationService.getUserName();

    if (!callbackId) {
      callbackId = `dispatcher-${username}`;
    }

    const isDispatcherRole =
      AuthenticationService.allowMCXDispatcher() ||
      AuthenticationService.allowRailwayDispatcher();

    if (!isDispatcherRole) {
      return false;
    }

    const fullTopic = `/app/v2/messaging/topic/dispatcher/notifications/user/${username}`;

    AuthenticationService.dispatcherCallBacks.set(callbackId, {
      eventTopic: fullTopic,
      eventCallback: callback,
    });

    if (!this.dispatcherEvents.includes(fullTopic)) {
      this.dispatcherEvents.push(fullTopic);
    }

    if (!this.connected) {
      this.connect();
    }

    return true;
  }

  releaseDispatcher(callbackId) {
    if (!callbackId) {
      return false;
    }

    AuthenticationService.dispatcherCallBacks.delete(callbackId);
    return true;
  }

  receiveMessageForRouteCoordinate(
    topic = "routeCoordinate/live",
    callback,
    callbackId
  ) {
    if (!callbackId) callbackId = topic;

    const fullTopic = `/app/v2/messaging/topic/${topic}`;

    AuthenticationService.routeCoordinateCallBacks.set(callbackId, {
      eventTopic: fullTopic,
      eventCallback: callback,
    });

    if (!this.routeCoordinateEvents.includes(fullTopic)) {
      this.routeCoordinateEvents.push(fullTopic);
    }

    if (!this.connected) {
      this.connect();
    }

    return true;
  }

  releaseAllSubscriptions() {
    return true;
  }

  onNMSUpdate(message) {
    loggerFactory.debug(
      "NMS Message Received",
      `Message received on NMS Channel ${message.headers.destination}`
    );

    for (let [key, value] of AuthenticationService.nmsCallBacks) {
      if (message.headers.destination === value.eventTopic) {
        loggerFactory.debug(
          "Message Received ",
          `Message Received on NMS Channel for : events : forwarding message to : ${key}`,
          message.body
        );
        value.eventCallback(message);
      }
    }
  }

  onServiceUpdate(message) {
    loggerFactory.debug(
      "Service Message Received",
      `Message received on Service Channel ${message.headers.destination}`
    );

    for (let [key, value] of AuthenticationService.serviceCallBacks) {
      if (message.headers.destination === value.eventTopic) {
        loggerFactory.debug(
          "Message Received ",
          `Message Received on Service Channel for : events : forwarding message to : ${key}`,
          message.body
        );
        value.eventCallback(message);
      }
    }
  }

  onTelemetryUpdate(message) {
    loggerFactory.debug(
      "Telemetry Message Received",
      `Message received on telemetry Channel ${message.headers.destination}`
    );

    for (let [key, value] of AuthenticationService.telemetryCallBacks) {
      if (message.headers.destination === value.eventTopic) {
        loggerFactory.debug(
          "Message Received ",
          `Message Received on telemetry Channel for : events : forwarding message to : ${key}`,
          message.body
        );
        value.eventCallback(message);
      }
    }
  }

  onDispatcherUpdate(message) {
    loggerFactory.debug(
      "Dispatcher Message Received",
      `Message received on Dispatcher Channel ${message.headers.destination}`
    );

    for (let [key, value] of AuthenticationService.dispatcherCallBacks) {
      if (message.headers.destination === value.eventTopic) {
        loggerFactory.debug(
          "Message Received ",
          `Message Received on Dispatcher Channel for : ${key}`,
          message.body
        );
        value.eventCallback(message);
      }
    }
  }

  onRouteCoordinateUpdate(message) {
    loggerFactory.debug(
      "RouteCoordinate",
      `Message on ${message.headers.destination}`,
      message.body
    );
    try {
      for (let [key, value] of AuthenticationService.routeCoordinateCallBacks) {
        value.eventCallback(message);
      }
    } catch (e) {
      console.error("Failed to parse route coordinate", e, message.body);
    }
  }

  onMCXUpdate(message) {
    loggerFactory.debug(
      "MCX Message Received",
      `Message received on MCX Channel ${message.headers.destination}`
    );
    if (AuthenticationService.mcxCallBacks) {
      for (let [key, value] of AuthenticationService.mcxCallBacks) {
        if (message.headers.destination === value.eventTopic) {
          loggerFactory.debug(
            "Message Received ",
            `Message Received on MCX Channel for : events : forwarding message to : ${key}`,
            message.body
          );

          value.eventCallback(message);
        }
      }
    }
  }
}

const NotificationManager = new RealtimeUpdate();

NotificationManager.connect();

export default NotificationManager;
